import { Entity } from '@core/entities/entity'
import { ICompetencyTemplate } from '@core/entities/competency/CompetencyTemplate/ICompetencyTemplate'
import { plainToInstance, Type } from 'class-transformer'
import { ProcessTag } from '@core/entities/processTags/ProcessTag'
import { CompetencyTemplateVersion } from '@core/entities/competency/CompetencyTemplateVersion'
import {
  CompetencyTemplatePermittedActions,
  CompetencyTemplateStatus,
} from '@core/entities/competency/CompetencyTemplate/types'
import { ICompetencyTemplateUser } from '@core/entities/competency/CompetencyTemplateUser'
import { statuses, StatusColor } from '@core/theme/statuses'

class CompetencyTemplateTagsWrapper {
  @Type(() => ProcessTag)
  tags?: Array<ProcessTag>
}

export class CompetencyTemplate extends Entity<ICompetencyTemplate> {
  id: number

  organisationId: number

  organisationName?: string

  title: string

  description?: string

  status: CompetencyTemplateStatus

  @Type(() => CompetencyTemplateVersion)
  version?: CompetencyTemplateVersion

  @Type(() => CompetencyTemplateTagsWrapper)
  tags: CompetencyTemplateTagsWrapper

  createdOn?: string

  modifiedOn?: string

  createdBy?: ICompetencyTemplateUser

  modifiedBy?: ICompetencyTemplateUser

  permittedActions: CompetencyTemplatePermittedActions

  public static new(payload: unknown): CompetencyTemplate {
    return plainToInstance(CompetencyTemplate, payload)
  }

  public isDraft(): boolean {
    return this.status === 'DRAFT'
  }

  public isActive(): boolean {
    return this.status === 'ACTIVE'
  }

  public isArchived(): boolean {
    return this.status === 'ARCHIVED'
  }

  public canEdit(): boolean {
    return this.permittedActions?.includes('EDIT') ?? false
  }

  public canArchive(): boolean {
    return this.permittedActions?.includes('ARCHIVE') ?? false
  }

  public canCreateVersion(): boolean {
    return this.canEdit() && !this.isArchived()
  }

  public canBeArchivedByUser(): boolean {
    return this.canArchive() && !this.isArchived()
  }

  public isReadOnly(): boolean {
    return !this.canEdit() || this.isArchived()
  }

  public getProcessTags(): Array<ProcessTag> {
    return this.tags?.tags ?? []
  }

  public static statusString(status: CompetencyTemplateStatus): string {
    switch (status) {
      case 'DRAFT':
        return statuses.draft.label
      case 'ACTIVE':
        return statuses.active.label
      case 'ARCHIVED':
        return statuses.archived.label
      default:
        return status
    }
  }

  public getStatusString(): string {
    return CompetencyTemplate.statusString(this.status)
  }

  public static statusChipColor(
    status: CompetencyTemplateStatus,
  ): StatusColor | 'default' {
    switch (status) {
      case 'ACTIVE':
        return statuses.active.color
      case 'DRAFT':
        return statuses.draft.color
      case 'ARCHIVED':
        return statuses.archived.color
      default:
        return 'default'
    }
  }

  public getStatusChipColor() {
    return CompetencyTemplate.statusChipColor(this.status)
  }
}
